// Categories & shared constants for SQPDCHI board
// Detail fields and legends are now stored per-category on each board.
// This file only holds category metadata (colors, icons, labels).

export const CATEGORIES = [
  { key: 'S', label: 'Safety',       color: '#ef4444', icon: 'ShieldAlert' },
  { key: 'Q', label: 'Quality',      color: '#f59e0b', icon: 'Award' },
  { key: 'P', label: 'Productivity', color: '#3b82f6', icon: 'TrendingUp' },
  { key: 'D', label: 'Delivery',     color: '#8b5cf6', icon: 'Truck' },
  { key: 'C', label: 'Cost',         color: '#059669', icon: 'DollarSign' },
  { key: 'H', label: 'Human Dev.',   color: '#0ea5e9', icon: 'Users' },
  { key: 'I', label: 'Inventory',    color: '#ec4899', icon: 'Package' },
];

export const CATEGORY_MAP = CATEGORIES.reduce((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

export const DEFAULT_LEGEND = [
  { key: 'green',  label: 'On Track', color: '#22c55e', bg: '#dcfce7', ring: '#16a34a' },
  { key: 'yellow', label: 'At Risk',  color: '#eab308', bg: '#fef9c3', ring: '#ca8a04' },
  { key: 'red',    label: 'Issue',    color: '#ef4444', bg: '#fee2e2', ring: '#dc2626' },
  { key: 'grey',   label: 'No Data',  color: '#94a3b8', bg: '#f1f5f9', ring: '#94a3b8' },
];

export const LEGEND_PALETTE = [
  { color: '#22c55e', bg: '#dcfce7', ring: '#16a34a' },
  { color: '#eab308', bg: '#fef9c3', ring: '#ca8a04' },
  { color: '#ef4444', bg: '#fee2e2', ring: '#dc2626' },
  { color: '#3b82f6', bg: '#dbeafe', ring: '#2563eb' },
  { color: '#8b5cf6', bg: '#ede9fe', ring: '#7c3aed' },
  { color: '#ec4899', bg: '#fce7f3', ring: '#db2777' },
  { color: '#0ea5e9', bg: '#e0f2fe', ring: '#0284c7' },
  { color: '#f97316', bg: '#ffedd5', ring: '#ea580c' },
  { color: '#94a3b8', bg: '#f1f5f9', ring: '#94a3b8' },
  { color: '#1e293b', bg: '#e2e8f0', ring: '#0f172a' },
];

export const FIELD_TYPES = [
  { key: 'text', label: 'Text' },
  { key: 'number', label: 'Number' },
  { key: 'textarea', label: 'Long text' },
  { key: 'select', label: 'Dropdown' },
];

export const TASK_STATUSES = [
  { key: 'open',        label: 'Open',        color: '#64748b' },
  { key: 'in_progress', label: 'In Progress', color: '#3b82f6' },
  { key: 'closed',      label: 'Closed',      color: '#16a34a' },
  { key: 'overdue',     label: 'Overdue',     color: '#dc2626' },
];

export const CLOSURE_TYPES = [
  { key: '24h', label: '24 Hour', days: 1 },
  { key: '3day', label: '3-Day', days: 3 },
  { key: 'long_term', label: 'Long Term', days: 30 },
];

export const TIERS = [
  { key: 'team', label: 'Team' },
  { key: 'department', label: 'Department' },
  { key: 'site', label: 'Site' },
  { key: 'global', label: 'Global' },
];

export const ANALYSIS_TEMPLATES = [
  { key: 'a3', label: 'A3 Report', icon: 'FileText', color: '#0284c7',
    description: 'Structured 8-step problem-solving report.',
    fields: [
      { name: 'background', label: 'Background', type: 'textarea' },
      { name: 'current_state', label: 'Current State', type: 'textarea' },
      { name: 'target_state', label: 'Target State', type: 'textarea' },
      { name: 'gap_analysis', label: 'Gap Analysis', type: 'textarea' },
      { name: 'root_cause', label: 'Root Cause', type: 'textarea' },
      { name: 'countermeasures', label: 'Countermeasures', type: 'textarea' },
      { name: 'action_plan', label: 'Action Plan', type: 'textarea' },
      { name: 'follow_up', label: 'Follow-Up', type: 'textarea' },
    ],
  },
  { key: 'five_why', label: '5-Why Analysis', icon: 'HelpCircle', color: '#7c3aed',
    description: 'Ask "why" five times to reach the true root cause.',
    fields: [
      { name: 'problem', label: 'Problem Statement', type: 'textarea' },
      { name: 'why1', label: 'Why #1', type: 'textarea' },
      { name: 'why2', label: 'Why #2', type: 'textarea' },
      { name: 'why3', label: 'Why #3', type: 'textarea' },
      { name: 'why4', label: 'Why #4', type: 'textarea' },
      { name: 'why5', label: 'Why #5', type: 'textarea' },
      { name: 'root_cause', label: 'Root Cause', type: 'textarea' },
      { name: 'countermeasure', label: 'Countermeasure', type: 'textarea' },
    ],
  },
  { key: 'fishbone', label: 'Fishbone (Ishikawa)', icon: 'GitBranch', color: '#16a34a',
    description: 'Categorised root cause diagram – 6M framework.',
    fields: [
      { name: 'problem', label: 'Problem Statement', type: 'textarea' },
      { name: 'man', label: 'Man (People)', type: 'textarea' },
      { name: 'machine', label: 'Machine', type: 'textarea' },
      { name: 'material', label: 'Material', type: 'textarea' },
      { name: 'method', label: 'Method', type: 'textarea' },
      { name: 'measurement', label: 'Measurement', type: 'textarea' },
      { name: 'mother_nature', label: 'Mother Nature', type: 'textarea' },
      { name: 'root_cause', label: 'Root Cause', type: 'textarea' },
    ],
  },
  { key: 'pareto', label: 'Pareto Analysis', icon: 'BarChart3', color: '#ea580c',
    description: '80/20 prioritisation of defect / issue categories.',
    fields: [
      { name: 'problem', label: 'Problem Studied', type: 'textarea' },
      { name: 'timeframe', label: 'Time Frame', type: 'text' },
      { name: 'total_events', label: 'Total Events', type: 'number' },
      { name: 'top_causes', label: 'Top Causes (one per line)', type: 'textarea' },
      { name: 'vital_few', label: 'Vital Few (~80% impact)', type: 'textarea' },
      { name: 'action_plan', label: 'Action Plan', type: 'textarea' },
    ],
  },
  { key: 'pdca', label: 'PDCA Cycle', icon: 'RefreshCw', color: '#0ea5e9',
    description: 'Plan – Do – Check – Act improvement loop.',
    fields: [
      { name: 'problem', label: 'Problem / Opportunity', type: 'textarea' },
      { name: 'plan', label: 'Plan', type: 'textarea' },
      { name: 'do', label: 'Do', type: 'textarea' },
      { name: 'check', label: 'Check', type: 'textarea' },
      { name: 'act', label: 'Act', type: 'textarea' },
    ],
  },
  { key: 'dmaic', label: 'DMAIC', icon: 'Target', color: '#c026d3',
    description: 'Six-Sigma Define – Measure – Analyse – Improve – Control.',
    fields: [
      { name: 'define', label: 'Define', type: 'textarea' },
      { name: 'measure', label: 'Measure', type: 'textarea' },
      { name: 'analyse', label: 'Analyse', type: 'textarea' },
      { name: 'improve', label: 'Improve', type: 'textarea' },
      { name: 'control', label: 'Control', type: 'textarea' },
    ],
  },
  { key: 'kaizen', label: 'Kaizen Event', icon: 'Zap', color: '#f59e0b',
    description: 'Small, rapid continuous improvement action.',
    fields: [
      { name: 'observation', label: 'Observation', type: 'textarea' },
      { name: 'idea', label: 'Improvement Idea', type: 'textarea' },
      { name: 'expected_benefit', label: 'Expected Benefit', type: 'textarea' },
      { name: 'implementation', label: 'Implementation Steps', type: 'textarea' },
      { name: 'verification', label: 'Verification Result', type: 'textarea' },
    ],
  },
];

export const TEMPLATE_MAP = ANALYSIS_TEMPLATES.reduce((acc, t) => {
  acc[t.key] = t;
  return acc;
}, {});

// Resolve a per-category config (statuses + fields) from a board object.
export function getCategoryConfig(board, categoryKey) {
  const cfg = board?.category_configs?.[categoryKey];
  if (cfg && cfg.statuses && cfg.statuses.length > 0) return cfg;
  return { statuses: DEFAULT_LEGEND, fields: [] };
}
